import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useCreateCustomerGroupMutation,
  useUpdateCustomerGroupMutation,
  useGetCustomerGroupQuery,
} from '../store/services/customerApi'
import { CustomerGroupCreateData, CustomerGroupUpdateData } from '../types/customer'
import PageHeader from '../components/ui/PageHeader'

interface MOQRule {
  product_id?: string
  category_id?: string
  min_quantity: number
}

interface MOARule {
  customer_type?: string
  min_amount: number
}

const CustomerGroupFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const isEditing = Boolean(id)

  // Form state
  const [formData, setFormData] = useState<CustomerGroupCreateData>({
    name: '',
    description: '',
    rules: {},
    default_credit_limit: 0,
    default_credit_days: 30,
    moq_rules: {},
    moa_rules: {},
    is_active: true,
  })

  const [moqRules, setMoqRules] = useState<MOQRule[]>([])
  const [moaRules, setMoaRules] = useState<MOARule[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // API hooks
  const { data: group, isLoading: groupLoading } = useGetCustomerGroupQuery(id!, {
    skip: !isEditing,
  })
  
  const [createCustomerGroup] = useCreateCustomerGroupMutation()
  const [updateCustomerGroup] = useUpdateCustomerGroupMutation()

  // Initialize form data when group changes
  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name,
        description: group.description || '',
        rules: group.rules || {},
        default_credit_limit: group.default_credit_limit,
        default_credit_days: group.default_credit_days,
        moq_rules: group.moq_rules || {},
        moa_rules: group.moa_rules || {},
        is_active: group.is_active,
      })

      // Parse MOQ rules
      const parsedMoqRules: MOQRule[] = []
      if (group.moq_rules && typeof group.moq_rules === 'object') {
        Object.entries(group.moq_rules).forEach(([key, value]) => {
          if (typeof value === 'number') {
            if (key.startsWith('product_')) {
              parsedMoqRules.push({
                product_id: key.replace('product_', ''),
                min_quantity: value,
              })
            } else if (key.startsWith('category_')) {
              parsedMoqRules.push({
                category_id: key.replace('category_', ''),
                min_quantity: value,
              })
            }
          }
        })
      }
      setMoqRules(parsedMoqRules)

      // Parse MOA rules
      const parsedMoaRules: MOARule[] = []
      if (group.moa_rules && typeof group.moa_rules === 'object') {
        Object.entries(group.moa_rules).forEach(([key, value]) => {
          if (typeof value === 'number') {
            parsedMoaRules.push({
              customer_type: key,
              min_amount: value,
            })
          }
        })
      }
      setMoaRules(parsedMoaRules)
    }
    setErrors({})
  }, [group])

  const handleInputChange = (field: keyof CustomerGroupCreateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const addMoqRule = () => {
    setMoqRules(prev => [...prev, { min_quantity: 1 }])
  }

  const updateMoqRule = (index: number, field: keyof MOQRule, value: any) => {
    setMoqRules(prev => prev.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    ))
  }

  const removeMoqRule = (index: number) => {
    setMoqRules(prev => prev.filter((_, i) => i !== index))
  }

  const addMoaRule = () => {
    setMoaRules(prev => [...prev, { min_amount: 0 }])
  }

  const updateMoaRule = (index: number, field: keyof MOARule, value: any) => {
    setMoaRules(prev => prev.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    ))
  }

  const removeMoaRule = (index: number) => {
    setMoaRules(prev => prev.filter((_, i) => i !== index))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required'
    }

    if (formData.default_credit_limit !== undefined && formData.default_credit_limit < 0) {
      newErrors.default_credit_limit = 'Credit limit cannot be negative'
    }

    if (formData.default_credit_days !== undefined && formData.default_credit_days < 0) {
      newErrors.default_credit_days = 'Credit days cannot be negative'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    // Convert MOQ rules to object format
    const moqRulesObj: Record<string, number> = {}
    moqRules.forEach(rule => {
      if (rule.product_id) {
        moqRulesObj[`product_${rule.product_id}`] = rule.min_quantity
      } else if (rule.category_id) {
        moqRulesObj[`category_${rule.category_id}`] = rule.min_quantity
      }
    })

    // Convert MOA rules to object format
    const moaRulesObj: Record<string, number> = {}
    moaRules.forEach(rule => {
      if (rule.customer_type) {
        moaRulesObj[rule.customer_type] = rule.min_amount
      }
    })

    const submitData = {
      ...formData,
      moq_rules: moqRulesObj,
      moa_rules: moaRulesObj,
    }

    setLoading(true)
    try {
      if (isEditing && group) {
        const updateData: CustomerGroupUpdateData = submitData
        await updateCustomerGroup({ id: group.id, data: updateData }).unwrap()
        enqueueSnackbar('Customer group updated successfully', { variant: 'success' })
      } else {
        await createCustomerGroup(submitData).unwrap()
        enqueueSnackbar('Customer group created successfully', { variant: 'success' })
      }
      navigate('/customers')
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.detail || 'Failed to save customer group',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/customers')
  }

  if (groupLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading customer group...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/customers')}
          sx={{ textDecoration: 'none' }}
        >
          Customers
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Customer Group' : 'Add New Customer Group'}
        </Typography>
      </Breadcrumbs>

      <PageHeader
        title={isEditing ? 'Edit Customer Group' : 'Add New Customer Group'}
        subtitle={isEditing ? `Update group settings for ${group?.name}` : 'Create a new customer group with business rules'}
        actions={[
          {
            label: 'Cancel',
            onClick: handleCancel,
            variant: 'outlined',
            icon: <ArrowBackIcon />,
            disabled: loading,
          },
          {
            label: loading ? 'Saving...' : isEditing ? 'Update Group' : 'Create Group',
            onClick: handleSubmit,
            variant: 'contained',
            color: 'secondary',
            icon: <SaveIcon />,
            disabled: loading,
          },
        ]}
      />

      <Paper sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <GroupIcon color="primary" />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Group Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          {/* Default Credit Terms */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mt: 2, mb: 2 }}>
              Default Credit Terms
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Default Credit Limit (₹)"
              type="number"
              value={formData.default_credit_limit}
              onChange={(e) => handleInputChange('default_credit_limit', Number(e.target.value))}
              error={!!errors.default_credit_limit}
              helperText={errors.default_credit_limit}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Default Credit Days"
              type="number"
              value={formData.default_credit_days}
              onChange={(e) => handleInputChange('default_credit_days', Number(e.target.value))}
              error={!!errors.default_credit_days}
              helperText={errors.default_credit_days}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          {/* MOQ Rules */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Minimum Order Quantity (MOQ) Rules
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addMoqRule}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Add MOQ Rule
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {moqRules.map((rule, index) => (
              <Card key={index} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      MOQ Rule {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeMoqRule(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Product ID (optional)"
                        value={rule.product_id || ''}
                        onChange={(e) => updateMoqRule(index, 'product_id', e.target.value)}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Category ID (optional)"
                        value={rule.category_id || ''}
                        onChange={(e) => updateMoqRule(index, 'category_id', e.target.value)}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Min Quantity"
                        type="number"
                        value={rule.min_quantity}
                        onChange={(e) => updateMoqRule(index, 'min_quantity', Number(e.target.value))}
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {moqRules.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No MOQ rules defined. Add rules to enforce minimum order quantities.
              </Alert>
            )}
          </Grid>

          {/* MOA Rules */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Minimum Order Amount (MOA) Rules
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addMoaRule}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Add MOA Rule
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {moaRules.map((rule, index) => (
              <Card key={index} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      MOA Rule {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeMoaRule(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Customer Type"
                        value={rule.customer_type || ''}
                        onChange={(e) => updateMoaRule(index, 'customer_type', e.target.value)}
                        size="small"
                        placeholder="e.g., retail, wholesaler"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Minimum Amount (₹)"
                        type="number"
                        value={rule.min_amount}
                        onChange={(e) => updateMoaRule(index, 'min_amount', Number(e.target.value))}
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {moaRules.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No MOA rules defined. Add rules to enforce minimum order amounts.
              </Alert>
            )}
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleCancel}
                disabled={loading}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
                sx={{ minWidth: 160, borderRadius: 2 }}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Group' : 'Create Group'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default CustomerGroupFormPage